import React from "react";
import {
  Upload, FileArchive, Check, X, Edit, Trash2, Download, Power,
  Hash, Calendar, User, Archive, AlertCircle, BarChart3,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";
import { Header, Field, IconBtn, Modal } from "@/pages/admin/AdminBlog";

const MAX_BYTES = 500 * 1024 * 1024;

function humanSize(bytes) {
  if (bytes == null) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let v = Number(bytes), i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
}

export default function AdminAPK() {
  const [items, setItems] = React.useState([]);
  const [analytics, setAnalytics] = React.useState(null);
  const [showUpload, setShowUpload] = React.useState(false);
  const [editing, setEditing] = React.useState(null);

  const load = async () => {
    const [list, an] = await Promise.all([
      api.get("/admin/apk").then((r) => r.data),
      api.get("/admin/apk/analytics?days=30").then((r) => r.data).catch(() => null),
    ]);
    setItems(list);
    setAnalytics(an);
  };
  React.useEffect(() => { load(); }, []);

  const activate = async (id) => {
    try {
      await api.post(`/admin/apk/${id}/activate`);
      toast.success("APK activated");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this APK file permanently?")) return;
    try {
      await api.delete(`/admin/apk/${id}`);
      toast.success("APK deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };
  const downloadOne = (apk) => {
    const url = `${API_BASE}/download/version/${encodeURIComponent(apk.version)}`;
    window.open(url, "_blank");
  };

  return (
    <div data-testid="admin-apk">
      <Header title="APK Manager" onAdd={() => setShowUpload(true)} addLabel="Upload APK" />

      {/* Analytics row */}
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile icon={Archive} label="Total Versions" value={items.length} color="text-sky-400" />
        <Tile icon={Download} label="Total Downloads" value={analytics?.total_downloads ?? 0} color="text-emerald-400" />
        <Tile icon={BarChart3} label="Today" value={analytics?.downloads_today ?? 0} color="text-[#D4AF37]" />
        <Tile icon={Power} label="Active Version" value={items.find((x) => x.active)?.version ?? "—"} color="text-violet-400" />
      </div>

      {/* Daily chart */}
      {analytics && analytics.daily?.length > 0 && (
        <div className="mt-6 card-luxury p-6">
          <div className="overline mb-1">Downloads — last 30 days</div>
          <h3 className="font-[Outfit] text-xl text-white font-light">Daily APK downloads</h3>
          <div className="mt-4 h-[220px]" data-testid="apk-downloads-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={analytics.daily}>
                <defs>
                  <linearGradient id="apk-dl" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="date" stroke="#64748B" fontSize={10} />
                <YAxis stroke="#64748B" fontSize={10} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#030712", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12 }} />
                <Area type="monotone" dataKey="count" stroke="#10B981" strokeWidth={2} fill="url(#apk-dl)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Version history table */}
      <div className="mt-6 card-luxury overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead className="bg-white/5">
            <tr className="text-left text-xs uppercase tracking-widest text-slate-400">
              <th className="px-5 py-3">Version</th>
              <th className="px-5 py-3">Build</th>
              <th className="px-5 py-3">Size</th>
              <th className="px-5 py-3">Uploaded</th>
              <th className="px-5 py-3">Downloads</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3 w-40 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {items.map((a) => (
              <tr key={a.id} data-testid={`apk-row-${a.id}`}>
                <td className="px-5 py-3 text-white font-medium">v{a.version}</td>
                <td className="px-5 py-3 text-slate-400">#{a.build_number}</td>
                <td className="px-5 py-3 text-slate-400">{humanSize(a.file_size)}</td>
                <td className="px-5 py-3 text-slate-400 text-xs">{new Date(a.upload_time).toLocaleDateString()} <span className="block text-[10px] text-slate-500">{a.uploaded_by}</span></td>
                <td className="px-5 py-3 text-emerald-400 font-medium">{a.download_count}</td>
                <td className="px-5 py-3">
                  {a.active ? (
                    <span className="text-xs px-2 py-0.5 rounded-full border bg-emerald-500/10 text-emerald-300 border-emerald-500/20">Active</span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full border bg-slate-500/10 text-slate-400 border-slate-500/20">Inactive</span>
                  )}
                </td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <IconBtn onClick={() => downloadOne(a)} testId={`apk-dl-${a.id}`}><Download className="w-3.5 h-3.5" /></IconBtn>
                  {!a.active && (
                    <IconBtn onClick={() => activate(a.id)} testId={`apk-activate-${a.id}`}>
                      <Power className="w-3.5 h-3.5" />
                    </IconBtn>
                  )}
                  <IconBtn onClick={() => setEditing(a)} testId={`apk-edit-${a.id}`}><Edit className="w-3.5 h-3.5" /></IconBtn>
                  {!a.active && (
                    <IconBtn onClick={() => remove(a.id)} variant="danger" testId={`apk-del-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></IconBtn>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-500 text-sm">No APKs uploaded yet. Click &ldquo;Upload APK&rdquo; to get started.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showUpload && <UploadModal onClose={() => setShowUpload(false)} onDone={load} />}
      {editing && <EditModal apk={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function Tile({ icon: Icon, label, value, color }) {
  return (
    <div className="card-luxury p-5">
      <Icon className={`w-5 h-5 ${color}`} />
      <div className="mt-3 font-[Outfit] text-2xl text-white font-light">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">{label}</div>
    </div>
  );
}

function UploadModal({ onClose, onDone }) {
  const [file, setFile] = React.useState(null);
  const [version, setVersion] = React.useState("");
  const [build, setBuild] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [activate, setActivate] = React.useState(true);
  const [progress, setProgress] = React.useState(0);
  const [stage, setStage] = React.useState(""); // hashing | signing | uploading | finalizing
  const [uploading, setUploading] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [error, setError] = React.useState("");
  const xhrRef = React.useRef(null);
  const dropRef = React.useRef(null);

  const onPickFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".apk")) {
      setError("Only .apk files are accepted.");
      return;
    }
    if (f.size > MAX_BYTES) {
      setError("File exceeds the 500 MB limit.");
      return;
    }
    setError("");
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    onPickFile(f);
  };

  // Streaming SHA-256 in the browser (avoids loading whole file at once for very large files)
  const computeSha256 = async (f) => {
    const CHUNK = 4 * 1024 * 1024; // 4 MB
    // Use subtle only on the full buffer for files ≤ 128MB (fast). For larger, chunked via ArrayBuffer stream.
    if (f.size <= 128 * 1024 * 1024) {
      const buf = await f.arrayBuffer();
      const digest = await crypto.subtle.digest("SHA-256", buf);
      return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
    }
    // Fallback: SubtleCrypto has no streaming API in browsers, but we can read whole file.
    // Modern browsers handle up to ~2GB ArrayBuffer on desktop. If it fails, we skip and rely on Cloudinary etag.
    try {
      const buf = await f.arrayBuffer();
      const digest = await crypto.subtle.digest("SHA-256", buf);
      return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
    } catch {
      return ""; // fallback — backend will still accept without SHA256
    }
  };

  const uploadToCloudinary = (sign, blob, attempt = 0) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhrRef.current = xhr;
      xhr.open("POST", sign.upload_url);
      xhr.responseType = "json";
      xhr.upload.onprogress = (e) => {
        if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300 && xhr.response) resolve(xhr.response);
        else if (attempt < 2 && (xhr.status === 0 || xhr.status >= 500)) {
          // network / server error → retry
          setTimeout(() => uploadToCloudinary(sign, blob, attempt + 1).then(resolve, reject), 800 * (attempt + 1));
        } else reject(new Error(xhr.response?.error?.message || `Upload failed (HTTP ${xhr.status})`));
      };
      xhr.onerror = () => {
        if (attempt < 2) setTimeout(() => uploadToCloudinary(sign, blob, attempt + 1).then(resolve, reject), 800 * (attempt + 1));
        else reject(new Error("Network error during upload"));
      };
      xhr.onabort = () => reject(new Error("Upload cancelled"));

      const fd = new FormData();
      // IMPORTANT: strip .apk extension in multipart filename — Cloudinary blocks .apk by default policy.
      // Bytes are unchanged; public_id + fl_attachment restore original name on download.
      fd.append("file", blob, "payload");
      fd.append("api_key", sign.api_key);
      fd.append("timestamp", sign.timestamp);
      fd.append("signature", sign.signature);
      fd.append("public_id", sign.public_id);
      fd.append("use_filename", "false");
      fd.append("unique_filename", "false");
      fd.append("overwrite", "false");
      xhr.send(fd);
    });

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { setError("Please select an .apk file."); return; }
    if (!version || !build) { setError("Version and build number are required."); return; }
    setError(""); setUploading(true); setProgress(0);

    try {
      // 1. Compute SHA-256 client-side (for integrity + duplicate detection)
      setStage("hashing");
      const sha256 = await computeSha256(file);

      // 2. Ask backend for a signed upload
      setStage("signing");
      const { data: sign } = await api.post("/admin/apk/sign-upload", {
        version, filename: file.name,
      });

      // 3. Upload directly to Cloudinary (bypasses our origin & any Cloudflare limit)
      setStage("uploading");
      setProgress(0);
      const cloud = await uploadToCloudinary(sign, file);

      // 4. Finalize on backend — save metadata
      setStage("finalizing");
      await api.post("/admin/apk/finalize", {
        version,
        build_number: Number(build),
        release_notes: notes,
        activate,
        original_filename: file.name,
        cloudinary_public_id: cloud.public_id,
        cloudinary_secure_url: cloud.secure_url,
        cloudinary_resource_type: cloud.resource_type || "raw",
        cloudinary_etag: cloud.etag,
        cloudinary_version: cloud.version,
        bytes: cloud.bytes || file.size,
        sha256,
      });

      setDone(true);
      toast.success("APK uploaded to Cloudinary and activated");
      setTimeout(() => { onDone(); onClose(); }, 1100);
    } catch (err) {
      const status = err.response?.status;
      let msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      if (status === 413) {
        msg = "Upload blocked by the gateway (HTTP 413). The reverse proxy in front of the site rejected the file. Since we now upload directly to Cloudinary, this should never happen — ensure the upload URL is api.cloudinary.com.";
      }
      setError(msg);
      setUploading(false);
    }
  };

  const stageLabel = {
    hashing: "Computing checksum…",
    signing: "Requesting upload signature…",
    uploading: "Uploading to Cloudinary…",
    finalizing: "Saving version…",
  }[stage] || "";

  return (
    <Modal title="Upload New APK" onClose={uploading ? () => {} : onClose}>
      {done ? (
        <div className="text-center py-8" data-testid="apk-upload-success">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="w-14 h-14 mx-auto rounded-full bg-emerald-500/15 ring-1 ring-emerald-500/40 flex items-center justify-center">
            <Check className="w-7 h-7 text-emerald-400" />
          </motion.div>
          <div className="mt-4 font-[Outfit] text-xl text-white font-light">APK uploaded.</div>
          <div className="text-slate-400 text-sm mt-1">Now serving v{version} to all users.</div>
        </div>
      ) : (
      <form onSubmit={submit} className="space-y-5">
        {/* drop zone */}
        <div
          ref={dropRef}
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          onClick={() => document.getElementById("apk-file-input").click()}
          className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-colors ${
            file ? "border-emerald-500/40 bg-emerald-500/5" : "border-white/15 hover:border-sky-500/40 bg-white/5"
          }`}
          data-testid="apk-dropzone"
        >
          <input
            id="apk-file-input"
            type="file"
            accept=".apk,application/vnd.android.package-archive"
            className="hidden"
            onChange={(e) => onPickFile(e.target.files?.[0])}
            data-testid="apk-file-input"
          />
          {file ? (
            <div>
              <FileArchive className="w-9 h-9 text-emerald-400 mx-auto" />
              <div className="mt-3 text-white text-sm font-medium">{file.name}</div>
              <div className="text-slate-400 text-xs mt-1">{humanSize(file.size)}</div>
              <button type="button" onClick={(e) => { e.stopPropagation(); setFile(null); }} className="text-xs text-rose-400 mt-2 hover:text-rose-300">Remove</button>
            </div>
          ) : (
            <div>
              <Upload className="w-9 h-9 text-sky-400 mx-auto" />
              <div className="mt-3 text-white text-sm">Drag & drop your <span className="text-sky-400">.apk</span> here</div>
              <div className="text-slate-500 text-xs mt-1">or click to browse · max 500 MB</div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Version (semver)">
            <Input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="1.0.0" required data-testid="apk-version-input" />
          </Field>
          <Field label="Build number">
            <Input type="number" value={build} onChange={(e) => setBuild(e.target.value)} placeholder="100" required data-testid="apk-build-input" />
          </Field>
        </div>
        <Field label="Release notes">
          <Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What's new in this version…" data-testid="apk-notes-input" />
        </Field>

        <div className="flex items-center gap-3 text-sm">
          <Switch checked={activate} onCheckedChange={setActivate} />
          <span className="text-slate-300">Make active immediately (deactivates current)</span>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-rose-400 text-sm bg-rose-500/5 border border-rose-500/20 rounded-lg px-3 py-2" data-testid="apk-upload-error">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}

        {uploading && (
          <div data-testid="apk-upload-progress">
            <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
              <span>{stageLabel}</span>
              <span className="text-sky-400 font-medium">{stage === "uploading" ? `${progress}%` : ""}</span>
            </div>
            <div className="h-2 rounded-full bg-white/5 overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-sky-500 to-[#D4AF37]"
                animate={{ width: stage === "uploading" ? `${progress}%` : stage === "finalizing" ? "100%" : "12%" }}
                transition={{ duration: 0.3 }}
              />
            </div>
            <div className="mt-1 text-[10px] text-slate-500">
              Direct browser → Cloudinary. Bytes never touch our server.
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" disabled={uploading} onClick={onClose} className="btn-ghost">Cancel</button>
          <button type="submit" disabled={uploading || !file} className="btn-primary disabled:opacity-60" data-testid="apk-upload-submit">
            {uploading ? "Uploading…" : "Upload APK"}
          </button>
        </div>
      </form>
      )}
    </Modal>
  );
}

function EditModal({ apk, onClose, onDone }) {
  const [version, setVersion] = React.useState(apk.version);
  const [build, setBuild] = React.useState(apk.build_number);
  const [notes, setNotes] = React.useState(apk.release_notes || "");
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/admin/apk/${apk.id}`, { version, build_number: Number(build), release_notes: notes });
      toast.success("Saved");
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };
  return (
    <Modal title={`Edit v${apk.version}`} onClose={onClose}>
      <form onSubmit={save} className="space-y-4">
        <div className="card-luxury p-4 grid grid-cols-2 gap-3 text-xs text-slate-400">
          <div className="flex items-center gap-2"><Hash className="w-3.5 h-3.5 text-sky-400" />{apk.sha256.slice(0, 18)}…</div>
          <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-sky-400" />{new Date(apk.upload_time).toLocaleString()}</div>
          <div className="flex items-center gap-2"><User className="w-3.5 h-3.5 text-sky-400" />{apk.uploaded_by}</div>
          <div className="flex items-center gap-2"><FileArchive className="w-3.5 h-3.5 text-sky-400" />{humanSize(apk.file_size)}</div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Version"><Input value={version} onChange={(e) => setVersion(e.target.value)} required /></Field>
          <Field label="Build number"><Input type="number" value={build} onChange={(e) => setBuild(e.target.value)} required /></Field>
        </div>
        <Field label="Release notes"><Textarea rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <button type="submit" className="btn-primary" data-testid="apk-edit-save">Save</button>
        </div>
      </form>
    </Modal>
  );
}
